import { useCallback } from 'react';

/**
 * Default DOM ids of the Spark chat widget's container and button, matching the
 * fallbacks baked into the external SparkWidget bundle. A page can mount more
 * than one Spark button (e.g. the charger-summary "Analyze" button and the
 * session-banner "Learn more" button); each is given its own container/button
 * id so their params stay isolated, and these defaults are used when a caller
 * doesn't supply one.
 */
export const DEFAULT_SPARK_CHAT_CONTAINER_ID = 'chat-app-container';
export const DEFAULT_SPARK_CHAT_BUTTON_ID = 'analyze-with-spark-button-id';

const getSparkChatButton = (buttonId: string): HTMLButtonElement | null =>
  document.getElementById(buttonId) as HTMLButtonElement | null;

/**
 * Hook that exposes a stable function for opening a specific Spark chat button.
 *
 * The Spark chat is rendered by an external script (`window.SparkWidget`) and
 * exposes itself only as a button in the DOM. We don't own that button, so the
 * only public way to open the chat is to dispatch a click on it. Centralizing
 * this fragile bridge in one hook means callers don't have to know about the
 * button id, and we can harden the trigger here later (waiting for the button
 * to mount, logging when it's missing, etc.) without touching every call site.
 *
 * Pass the `buttonId` of the specific widget instance to open; defaults to the
 * primary button. The returned function is a no-op when the button is not yet
 * rendered or is disabled.
 */
export const useOpenSparkChat = (buttonId: string = DEFAULT_SPARK_CHAT_BUTTON_ID): (() => void) => {
  return useCallback(() => {
    const sparkButton = getSparkChatButton(buttonId);
    if (sparkButton && !sparkButton.disabled) {
      sparkButton.click();
    }
  }, [buttonId]);
};

/**
 * Hook that exposes a stable function for toggling the disabled state of a
 * specific Spark chat button. Like `useOpenSparkChat`, this exists so consumers
 * don't reach into the DOM directly — the button id stays an implementation
 * detail of this module.
 *
 * The returned function is a no-op when the button is not yet rendered.
 */
export const useSetSparkChatDisabled = (
  buttonId: string = DEFAULT_SPARK_CHAT_BUTTON_ID,
): ((disabled: boolean) => void) => {
  return useCallback(
    (disabled: boolean) => {
      const sparkButton = getSparkChatButton(buttonId);
      if (sparkButton) {
        sparkButton.disabled = disabled;
      }
    },
    [buttonId],
  );
};
